import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

import Navbar   from './components/Navbar';
import Footer   from './components/Footer';

import Home           from './pages/Home';
import About          from './pages/About';
import Contact        from './pages/Contact';
import Plan           from './pages/Plan';
import Register       from './pages/Register';
import Login          from './pages/Login';
import Payment        from './pages/Payment';
import Pending        from './pages/Pending';
import Dashboard      from './pages/Dashboard';
import ProfileView    from './pages/ProfileView';
import AdminDashboard from './pages/AdminDashboard';
import { Privacy, Terms, Refund } from './pages/LegalPages';

import './pages/LegalPages.css';

// ── PROTECTED ROUTE ──────────────────────────────────────────────────────────
function Protected({ children, requireAdmin = false, requireApproved = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{paddingTop:'72px'}}><div className="loader"><div className="spinner" /></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (requireAdmin && user.role !== 'admin') return <Navigate to="/" replace />;
  if (requireApproved && user.status !== 'approved' && user.role !== 'admin') return <Navigate to="/pending" replace />;
  return children;
}

// ── LAYOUT with Navbar+Footer ─────────────────────────────────────────────────
function Layout({ children, hideFooter = false }) {
  return (
    <>
      <Navbar />
      <main style={{ minHeight: 'calc(100vh - 72px)' }}>{children}</main>
      {!hideFooter && <Footer />}
    </>
  );
}

// ── APP ROUTES ────────────────────────────────────────────────────────────────
function AppRoutes() {
  return (
    <Routes>
      {/* PUBLIC */}
      <Route path="/"        element={<Layout><Home /></Layout>} />
      <Route path="/about"   element={<Layout><About /></Layout>} />
      <Route path="/contact" element={<Layout><Contact /></Layout>} />
      <Route path="/plan"    element={<Layout><Plan /></Layout>} />
      <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
      <Route path="/terms"   element={<Layout><Terms /></Layout>} />
      <Route path="/refund"  element={<Layout><Refund /></Layout>} />

      {/* AUTH */}
      <Route path="/login"    element={<Layout hideFooter><Login /></Layout>} />
      <Route path="/register" element={<Layout hideFooter><Register /></Layout>} />

      {/* POST-REGISTER */}
      <Route path="/payment" element={
        <Protected>
          <Layout><Payment /></Layout>
        </Protected>
      } />
      <Route path="/pending" element={
        <Protected>
          <Layout><Pending /></Layout>
        </Protected>
      } />

      {/* CLIENT - APPROVED ONLY */}
      <Route path="/dashboard" element={
        <Protected requireApproved>
          <Layout hideFooter><Dashboard /></Layout>
        </Protected>
      } />
      <Route path="/profile/:id" element={
        <Protected requireApproved>
          <Layout><ProfileView /></Layout>
        </Protected>
      } />

      {/* ADMIN */}
      <Route path="/admin" element={
        <Protected requireAdmin>
          <Layout hideFooter><AdminDashboard /></Layout>
        </Protected>
      } />

      {/* FALLBACK */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3500,
            style: {
              fontFamily: "'Nunito', sans-serif",
              fontSize: '14px',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(63,13,12,0.15)',
            },
            success: { style: { background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb' } },
            error:   { style: { background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' } },
          }}
        />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
